// components/Navbar.js
import { useState, useEffect, useRef } from 'react';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { useApp } from '../context/AppContext';
import { collection, query, where, onSnapshot, doc, onSnapshot as onDocSnapshot, orderBy, limit } from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext'; // Corrigido o caminho
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { ptBR } from 'date-fns/locale';

function Navbar({ onProfileClick }) {
  const { state } = useApp();
  const { totalPrice } = state;
  const [totalInvested, setTotalInvested] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hasApps, setHasApps] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [pendingAppsCount, setPendingAppsCount] = useState(0);
  const [recentPendingApps, setRecentPendingApps] = useState([]);
  const [recentNewUsers, setRecentNewUsers] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);

  // Calcular contagem total de notificações
  useEffect(() => {
    const notifyUsers = recentNewUsers.filter(u => {
      if (u.isNewUser) return true;
      if (u.userStatus === 'pending') return true;
      if (u.createdAt?.toDate) {
        return (new Date() - u.createdAt.toDate()) < 24 * 60 * 60 * 1000;
      }
      return false;
    });
    setPendingAppsCount(recentPendingApps.length + notifyUsers.length);
  }, [recentPendingApps, recentNewUsers]);

  // Inicializar com a foto do usuário atual se disponível, para evitar delay
  const [profileImageUrl, setProfileImageUrl] = useState(auth.currentUser?.photoURL || null);
  const [userName, setUserName] = useState(auth.currentUser?.displayName || '');
  const [greeting, setGreeting] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  
  // Usar o hook do tema
  const { isDarkMode } = useTheme();

  // Função para determinar a saudação com base no horário
  const getGreeting = () => {
    const hour = new Date().getHours();
    
    if (hour >= 5 && hour < 12) {
      return 'Bom dia';
    } else if (hour >= 12 && hour < 18) {
      return 'Boa tarde';
    } else {
      return 'Boa noite';
    }
  };

  // Atualizar a saudação periodicamente (a cada hora)
  useEffect(() => {
    // Definir a saudação inicial
    setGreeting(getGreeting());
    
    // Atualizar a saudação a cada hora
    const interval = setInterval(() => {
      setGreeting(getGreeting());
    }, 3600000); // 1 hora em milissegundos
    
    return () => clearInterval(interval);
  }, []);

    // Fechar dropdown de notificações ao clicar fora
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (notificationRef.current && !notificationRef.current.contains(event.target)) {
          setShowNotifications(false);
        }
      };
  
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

  // Buscar o total investido, verificar se usuário tem apps, buscar imagem de perfil e nome do usuário
  useEffect(() => {
    // ✅ Usar onAuthStateChanged para garantir que o usuário está autenticado
    const unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
      if (user) {
        // ✅ Definir imagem inicial do Auth imediatamente para evitar delay
        if (user.photoURL) {
          setProfileImageUrl(user.photoURL);
        }

        // ✅ Usar listener em tempo real para dados do usuário
        const userDocRef = doc(db, 'users', user.uid);
        let unsubscribeAdminApps = () => {}; // Placeholder para cleanup

        const unsubscribeUserDoc = onDocSnapshot(userDocRef, (userDoc) => {
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            // Lógica unificada para imagem de perfil
            // Prioridade: Imagem salva no banco > Imagem do Google > null
            const finalImage = userData.profileImage || user.photoURL;
            if (finalImage) {
              setProfileImageUrl(finalImage);
            }
            
            // Buscar o nome do usuário (pode estar em diferentes campos)
            if (userData.fullName) {
              setUserName(userData.fullName.split(' ')[0]); // Primeiro nome
            } else if (userData.name) {
              setUserName(userData.name);
            } else if (userData.displayName) {
              setUserName(userData.displayName);
            } else if (userData.email) {
              // Usar parte do email se não tiver nome
              setUserName(userData.email.split('@')[0]);
            }

            // ✅ Verificar se é admin e configurar listener de notificações
          const userIsAdmin = userData.isAdmin === true;
          setIsAdmin(userIsAdmin);

          if (userIsAdmin) {
            // Cancelar listener anterior se existir
            unsubscribeAdminApps();
            
            // Listener para Apps Pendentes
            const adminAppsQuery = query(
              collection(db, 'apps'),
              where('status', 'in', ['pending', 'pending_payment']),
              orderBy('createdAt', 'desc')
            );

            // Listener para Novos Usuários
            const adminUsersQuery = query(
              collection(db, 'users'),
              orderBy('createdAt', 'desc'),
              limit(10)
            );

            const unsubApps = onSnapshot(adminAppsQuery, (snapshot) => {
              const pendingApps = snapshot.docs.map(doc => ({
                id: doc.id,
                type: 'app',
                ...doc.data()
              }));
              setRecentPendingApps(pendingApps);
            });

            const unsubUsers = onSnapshot(adminUsersQuery, (userSnapshot) => {
              const newUsers = userSnapshot.docs.map(doc => ({
                id: doc.id,
                type: 'user',
                ...doc.data()
              }));
              setRecentNewUsers(newUsers);
            });

            unsubscribeAdminApps = () => {
              unsubApps();
              unsubUsers();
            };
          }
        }
      }, (error) => {
          console.error('Erro ao buscar dados do usuário:', error);
        });

        // Verificar se usuário tem apps
        const appsQuery = query(
          collection(db, 'apps'),
          where('userId', '==', user.uid)
        );
        
        const unsubscribeApps = onSnapshot(appsQuery, (appsSnapshot) => {
          setHasApps(!appsSnapshot.empty);
          
          if (location.pathname === '/dashboard') {
            const total = appsSnapshot.docs.reduce((sum, doc) => {
              const appData = doc.data();
              return sum + (appData.totalPrice || 0);
            }, 0);
            setTotalInvested(total);
            setLoading(false);
          }
        });
        
        // Retornar função de cleanup para todos os listeners
        return () => {
          unsubscribeUserDoc();
          unsubscribeApps();
          unsubscribeAdminApps();
        };
      } else {
        setLoading(false);
      }
    });

    // Cleanup do auth observer
    return () => unsubscribeAuth();
  }, [location.pathname]);

  // ✅ Observar mudanças no totalPrice do contexto para atualizar em tempo real
  useEffect(() => {
    // Este useEffect garante que o componente seja re-renderizado quando totalPrice mudar
  }, [totalPrice]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  const handleDashboard = () => {
    navigate('/dashboard');
  };

  // Função para formatar preço
  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price || 0);
  };

  // Fechar menu ao clicar em um link (para mobile)
  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600&display=swap');
          .greeting-text {
            font-family: 'Poppins', sans-serif;
          }
        `}
      </style>
      
      <div className="flex justify-end items-center mb-8 gap-4">

        
        {/* Desktop Menu - Valores, Tema, Dashboard e Logout (lado direito) */}
        <div className="hidden md:flex items-center space-x-4">
          {/* 🔔 Notificações (Apenas Admin) */}
          {isAdmin && (
            <div className="relative" ref={notificationRef}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className={`relative flex items-center justify-center p-2 rounded-xl transition-colors duration-200 text-gray-500 hover:bg-gray-50 hover:text-gray-900 border border-transparent hover:border-gray-100 ${showNotifications ? 'bg-gray-50 border-gray-100 text-gray-900' : ''}`}
                title="Notificações de novos apps"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {pendingAppsCount > 0 && (
                  <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-white dark:ring-gray-800">
                    {pendingAppsCount > 9 ? '9+' : pendingAppsCount}
                  </span>
                )}
              </button>

              {/* Dropdown de Notificações */}
              {showNotifications && (
                <div className={`absolute right-0 mt-2 w-80 rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 overflow-hidden ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-100'}`}>
                  <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                    <div className="flex justify-between items-center">
                      <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Notificações</h3>
                      {pendingAppsCount > 0 && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                          {pendingAppsCount} nova{pendingAppsCount !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="max-h-[300px] overflow-y-auto">
                    {(recentPendingApps.length > 0 || recentNewUsers.filter(u => u.isNewUser || (u.createdAt?.toDate && (new Date() - u.createdAt.toDate() < 24 * 60 * 60 * 1000))).length > 0) ? (
                      <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                        {/* Renderizar Apps Pendentes */}
                        {recentPendingApps.map((app) => (
                          <li key={app.id}>
                            <button
                              onClick={() => {
                                navigate('/admin');
                                setShowNotifications(false);
                              }}
                              className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex items-start gap-3 group`}
                            >
                              <div className="flex-shrink-0 mt-1">
                                <div className={`w-2 h-2 rounded-full ${app.status === 'pending_payment' ? 'bg-amber-400' : 'bg-green-400'}`}></div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                                  {app.name || 'App sem nome'}
                                </p>
                                <p className={`text-xs truncate mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  App • {app.userEmail}
                                </p>
                                <p className={`text-xs mt-1.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {app.createdAt ? formatDistanceToNow(app.createdAt.toDate(), { addSuffix: true, locale: ptBR }) : 'Recentemente'}
                                </p>
                              </div>
                            </button>
                          </li>
                        ))}

                        {/* Renderizar Novos Usuários */}
                        {recentNewUsers
                          .filter(u => u.isNewUser || u.userStatus === 'pending' || (u.createdAt?.toDate && (new Date() - u.createdAt.toDate() < 24 * 60 * 60 * 1000)))
                          .map((user) => (
                          <li key={user.id}>
                            <button
                              onClick={() => {
                                navigate('/admin');
                                setShowNotifications(false);
                              }}
                              className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex items-start gap-3 group`}
                            >
                              <div className="flex-shrink-0 mt-1">
                                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                                  Novo Usuário
                                </p>
                                <p className={`text-xs truncate mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {user.name || user.fullName || user.email}
                                </p>
                                <p className={`text-xs mt-1.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {user.createdAt ? formatDistanceToNow(user.createdAt.toDate(), { addSuffix: true, locale: ptBR }) : 'Recentemente'}
                                </p>
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="px-4 py-6 text-center">
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Nenhuma notificação nova
                        </p>
                      </div>
                    )}
                  </div>

                  <div className={`p-2 border-t ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-100 bg-gray-50'}`}>
                    <button
                      onClick={() => {
                        navigate('/admin');
                        setShowNotifications(false);
                      }}
                      className={`w-full py-2 text-xs font-medium text-center rounded-lg transition-colors ${
                        isDarkMode 
                          ? 'text-indigo-400 hover:bg-gray-700 hover:text-indigo-300' 
                          : 'text-indigo-600 hover:bg-white hover:text-indigo-700 hover:shadow-sm'
                      }`}
                    >
                      Ver todas as notificações
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}


          {location.pathname.includes('/app-creation') && totalPrice > 0 && (
            <div className={`flex items-center gap-3 px-4 py-2 rounded-full border shadow-sm transition-all duration-300
              ${isDarkMode ? 'bg-gray-800/80 border-gray-700 backdrop-blur-md' : 'bg-white border-gray-200'}
            `}>
              <div className={`flex items-center justify-center w-7 h-7 rounded-full 
                ${isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-600'}
              `}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex items-baseline gap-1.5 pt-0.5">
                <span className={`text-[11px] font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total:</span>
                <span className={`text-sm font-bold tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formatPrice(totalPrice)}</span>
              </div>
            </div>
          )}
          
          {hasApps && (
            <button 
              onClick={handleDashboard}
              className="flex items-center justify-center space-x-2 text-gray-500 hover:bg-gray-50 hover:text-gray-900 transition-all duration-200 px-3 py-2 rounded-xl border border-transparent hover:border-gray-100"
              title="Ir para Dashboard"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </button>
          )}

        {/* Botão de Perfil Minimalista */}
        <div className="relative group">
          <button 
            className="flex items-center gap-2 p-1 rounded-full hover:bg-gray-50 transition-colors"
          >
            {profileImageUrl ? (
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white shadow-sm">
                <img 
                  src={profileImageUrl} 
                  alt="Perfil" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `https://ui-avatars.com/api/?name=${userName || 'Usuário'}&background=random`;
                  }}
                />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center shadow-sm">
                <span className="text-white font-bold">{userName ? userName.charAt(0).toUpperCase() : 'U'}</span>
              </div>
            )}
          </button>
          
          {/* Dropdown de Logout Invisível até hover */}
          <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
            <div className="p-3 border-b border-gray-50 dark:border-gray-700/50">
              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{userName}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{greeting}</p>
            </div>
            <div className="p-2">
              <button
                onClick={handleLogout}
                className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 01-3-3h4a3 3 0 013 3v1" />
                </svg>
                Sair da conta
              </button>
            </div>
          </div>
        </div>
      </div>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Abrir menu"
        >
          {isMenuOpen ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 z-40 bg-black bg-opacity-50 transition-opacity duration-300" 
          onClick={closeMenu}
        ></div>
      )}

      {/* Mobile Menu Content - LATERAL DIREITO */}
      <div className={`md:hidden fixed top-0 right-0 z-50 h-full w-80 max-w-full bg-white dark:bg-gray-900 shadow-[0_8px_30px_rgb(0,0,0,0.12)] transform transition-transform duration-300 ease-in-out ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="p-5 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {profileImageUrl ? (
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-indigo-100">
                <img 
                  src={profileImageUrl} 
                  alt="Foto de perfil" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `https://ui-avatars.com/api/?name=${userName || 'Usuário'}&background=random`;
                  }}
                />
              </div>
            ) : (
              <div className="relative w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-md">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                
                {/* Indicador de que o perfil precisa ser configurado */}
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-2 w-2 text-white" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            )}
            
            {/* Saudação no menu mobile */}
            {userName && (
              <div className="greeting-text">
                <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">{greeting},</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{userName}</p>
              </div>
            )}
          </div>
          <button 
            onClick={closeMenu}
            className="p-2 rounded-xl text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-colors"
            aria-label="Fechar menu"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 overflow-y-auto h-full pb-20">
          {location.pathname.includes('/app-creation') && totalPrice > 0 && (
            <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 text-gray-900 dark:text-white p-4 rounded-2xl shadow-sm mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex flex-col">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Valor do App</span>
                  <span className="text-lg font-bold tracking-tight text-gray-900 dark:text-white">{formatPrice(totalPrice)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Mostrar total investido no dashboard */}
          {location.pathname === '/dashboard' && (
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200 px-4 py-3 rounded-xl font-semibold flex items-center space-x-2 shadow-sm mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>
                {loading ? 'Carregando...' : `Total Investido: ${formatPrice(totalInvested)}`}
              </span>
            </div>
          )}

          {/* Menu de Navegação */}
          <div className="mb-8">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 px-2">Navegação</h3>
            <ul className="space-y-1">
              {/* Link Admin com Notificação */}
              {isAdmin && (
                <li>
                  <button
                    onClick={() => {
                      navigate('/admin');
                      closeMenu();
                    }}
                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                      <span>Novos Aplicativos</span>
                    </div>
                    {pendingAppsCount > 0 && (
                      <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                        {pendingAppsCount > 9 ? '9+' : pendingAppsCount}
                      </span>
                    )}
                  </button>
                </li>
              )}

              {/* Dashboard aparece apenas se usuário tem apps */}
              {hasApps && (
                <li>
                  <a 
                    href="/dashboard" 
                    className="flex items-center space-x-3 px-3 py-2.5 rounded-xl text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-colors"
                    onClick={closeMenu}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                    <span>Dashboard</span>
                  </a>
                </li>
              )}
              <li>
                <a 
                  href="/profile" 
                  className="flex items-center space-x-3 px-3 py-2.5 rounded-xl text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-colors"
                  onClick={closeMenu}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>Meu Perfil</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Botão de Logout */}
          <button 
            onClick={() => {
              closeMenu();
              handleLogout();
            }}
            className="w-full flex items-center justify-center space-x-2 text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 transition-all duration-200 px-4 py-3 rounded-lg shadow-sm mt-6"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 01-3-3h4a3 3 0 013 3v1" />
            </svg>
            <span>Sair da conta</span>
          </button>
        </div>
      </div>
    </>
  );
}

export default Navbar;